<template>
    <div class="item-row px-3 py-2 d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
            <font-awesome-icon class="mr-3" :icon="['fas', 'grip-dots-vertical']" />
            <div class="d-flex align-items-center">
                <font-awesome-icon :icon="['fa-regular', 'map']" class="counter mr-2" />
                <div>
                    <span>{{ itemLabel }}</span>
                </div>
            </div>
        </div>
        <div class="d-flex justify-content-end">
            <tippy-component class="overflow-hidden ml-1 cursor-pointer" interactive>
                <template #trigger>
                    <c-button
                        size="icon"
                        :icon="[isDefault ? 'fas' : 'far', 'star']"
                        :disabled="readonly"
                        @click="handleDefaultStyle"
                    />
                </template>
                <span> Set as Default Style </span>
            </tippy-component>
            <c-button v-if="isEditable" size="icon" :icon="['far', 'pen']" :disabled="readonly" @click="handleOpen" />
            <c-button size="icon" :icon="['far', 'trash-can']" :disabled="readonly" @click="handleRemove" />
        </div>
        <c-modal :value="isOpen" title="Add a custom map style" size="lg" @closed="handleCancel">
            <div>
                <p>
                    Paste your public Style URL below to add it to your options.
                    <a href="https://docs.mapbox.com/help/glossary/style-url/" target="_blank" rel="noopener noreferrer"
                        >More info</a
                    >.
                </p>
                <c-input
                    v-model="newValue"
                    label="Custom Mapbox Style"
                    placeholder="mapbox://styles/username/style_id"
                />
            </div>
            <template #modal-footer>
                <div class="d-flex">
                    <c-button text="Cancel" variant="secondary" @click="handleCancel" />
                    <c-button text="Save" :disabled="!newValue" variant="primary" @click="handleUpdate" />
                </div>
            </template>
        </c-modal>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, inject, watch, Ref } from 'vue';
import CButton from '@/components/shared/CButton';
import CModal from '@/components/CModal.vue';
import CInput from '@/components/shared/CInput.vue';
import { MAP_STYLE_PRESETS } from '@/components/DesignStudio/MapStyleProperties/presets';
import { TippyComponent } from 'vue-tippy';

interface Props {
    id: number;
    item: string;
    readonly?: boolean;
    isDefault: boolean;
}

interface Emits {
    (
        event: 'update',
        data: {
            id: number;
            value: string;
        }
    ): void;
    (event: 'remove', value: string): void;
    (event: 'set-default', value: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const mapBoxPrefix = 'mapbox://styles/';

const isOpen = ref(false);
const newValue = ref(`${mapBoxPrefix}${props.item}`);

const openMapStyleId = inject<Ref<number | null>>('openMapStyleId', ref(null));

watch(
    () => openMapStyleId?.value,
    (val) => {
        if (val && val === props.id) {
            handleOpen();
            openMapStyleId!.value = null;
        }
    }
);

const mapStyle = computed(() => MAP_STYLE_PRESETS.find((preset) => preset.id === props.item));
const isEditable = computed(() => !MAP_STYLE_PRESETS.some((preset) => preset.id === props.item));
const itemLabel = computed(() => (mapStyle.value ? mapStyle.value.label : `Custom Style - ${props.item}`));

const handleClose = () => {
    isOpen.value = false;
};

const handleCancel = () => {
    newValue.value = `${mapBoxPrefix}${props.item}`;
    handleClose();
};

const handleOpen = () => {
    isOpen.value = true;
};

const handleUpdate = async () => {
    emit('update', {
        value: newValue.value.replace(mapBoxPrefix, ''),
        id: props.id
    });
    handleClose();
};

const handleRemove = () => {
    emit('remove', props.item);
};

const handleDefaultStyle = () => {
    emit('set-default', props.id);
};
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';
.item-row {
    cursor: pointer;
    border-bottom: 1px solid get-color('gray-light');
    .counter {
        color: #7258e0;
    }
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background: linear-gradient(90deg, #ede9fb 0%, #ebf9fa 100%);
    }
}
</style>
